import React from "react";
import {
    useScrollTrigger,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getDeviceType } from "redux/Selectors";
import { FormattedMessage, useIntl } from "react-intl";
import { ThreadRequest } from "api/requests/Requests";
import { UserModel, ThreadModel, CategoryModel } from "models/Models";
import { useNavigate, useParams, Link } from "react-router-dom";
import { pushDataLayer } from "gtm/gtm"
import { NetworkAction } from "redux/Actions";
import WriteIcon from "assets/images/write.svg";
import "styles/components/ThreadListComponent.scss";

type Props = {
    loginUser: UserModel | null;
    category: CategoryModel;
    langCd: string;
};

export const ThreadListComponent: React.FC<Props> = React.memo(
    (props) => {
        // Utility.log("@@@@@ ThreadListComponent IN");
        /***** 定数、変数 */
        const intl = useIntl();
        const navigate = useNavigate();
        const dispatch = useDispatch();
        const deviceType: number | null = useSelector((state) => getDeviceType(state));
        const { categoryId } = useParams<{ categoryId: string }>();

        /***** useState */
        // スレッドリスト
        const [lstThread, setThreadList] = React.useState<ThreadModel[]>([]);
        const [loadCompleted, setLoadCompleted] =
            React.useState<boolean>(false);
        // ダイアログ表示フラグ
        const [openDialog, setOpenDialog] = React.useState<boolean>(false);

        const trigger = useScrollTrigger({
            target: window,
            disableHysteresis: true,
            threshold: 300,
        });

        const scrollToTop = React.useCallback(() => {
            window.scrollTo({ top: 0 });
        }, []);

        /**
         * useEffect
         */
        React.useEffect(() => {
            pushDataLayer({
                event: 'page_view',
                screen: "スレッド一覧(掲示板)",
                path: window.location.pathname,
                deviceType: deviceType,
            });
        }, []);
        React.useEffect(() => {
            if (categoryId != null) {
                try {
                    const iCategoryId = parseInt(categoryId);
                    getThreadList(iCategoryId);
                } catch (error) {
                    console.error(error);
                }
            } else {
                setLoadCompleted(true);
            }
        }, [categoryId]);

        /**
         * ダイアログ表示
         */
        function handleDialogOpen() {
            setOpenDialog(true);
        }

        /**
         * ダイアログ閉じる
         */
        function handleDialogClose() {
            setOpenDialog(false);
        }

        /**
         * スレッド作成
         */
        function write() {
            if (props.loginUser == null) {
                handleDialogOpen();
                return;
            }
            if (categoryId == null) {
                return;
            }
            navigate(`/board/${categoryId}/thread/new`);
        }
        /**
         * スレッドリスト取得
         * @param index
         */
        async function getThreadList(categoryId: number) {
            let result = await ThreadRequest.getList(categoryId);
            if (result == null) {
                if (window.navigator.onLine) {
                    navigate("/maintenance");
                } else {
                    dispatch(NetworkAction({connected: false}));
                }
                return;
            }
            // スレッドリスト
            if (result.lstThread != null) {
                setThreadList(result.lstThread);
            }
            setLoadCompleted(true);
        }

        return (
            <div className="component ThreadListComponent">
                {loadCompleted && lstThread.length === 0 && (
                    <div className="no-thread-page">
                        <div className="no-thread-title">
                            <FormattedMessage id={"no_thread"} />
                        </div>
                        <div className="button-area">
                            <Button
                                className="app-button button"
                                variant="text"
                                onClick={write}
                            >
                                <FormattedMessage id="btn_create_thread" />
                            </Button>
                        </div>
                    </div>
                )}
                {loadCompleted && lstThread.length > 0 && (
                    <div className="contents-wrapper">
                        {lstThread.map((thread: ThreadModel, index) => {
                            return (
                                <React.Fragment key={`3_${index + 1}`}>
                                    {thread.title != null && (
                                        <Link
                                            className="thread-title"
                                            to={`/board/${categoryId}/thread/${thread.id}/list`}
                                        >
                                            {`${index + 1}.${thread.title}(${
                                                thread.numberOfResponse
                                            })`}
                                        </Link>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}
                <div className="icon-write" onClick={write}>
                    <OverlayTrigger
                        overlay={
                            <Popover id="tooltip-disabled" body>
                                <FormattedMessage
                                    id={"tooltip_create_thread"}
                                />
                            </Popover>
                        }
                    >
                        <img src={WriteIcon} alt="Create Thread Button" />
                    </OverlayTrigger>
                </div>
                <Dialog
                    className="dialog"
                    open={openDialog}
                    onClose={handleDialogClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle
                        id="alert-dialog-title"
                        className="dialog-title"
                    >
                        <FormattedMessage id={"dlg_title_message"} />
                    </DialogTitle>
                    <DialogContent className="dialog-content2">
                        <DialogContentText id="alert-dialog-description">
                            <FormattedMessage id={"msg_login_needed"} />
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            className="dialog-button"
                            onClick={handleDialogClose} 
                            color="primary">
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* <Zoom in={trigger}>
                    <Box
                        role="presentation"
                        sx={{
                            position: "fixed",
                            bottom: {
                                xs: 100,
                                sm: 32,
                            },
                            right: 10,
                            zIndex: 100,
                        }}
                    >
                        <Fab
                            onClick={scrollToTop}
                            color="primary"
                            size="small"
                            aria-label="scroll back to top"
                        >
                            <KeyboardArrowUp />
                        </Fab>
                    </Box>
                </Zoom> */}
            </div>
        );
    },
    (prevProps: Props, nextProps: Props) => {
        if (prevProps.loginUser !== nextProps.loginUser) {
            return false;
        }
        if (prevProps.category !== nextProps.category) {
            return false;
        }
        if (prevProps.category != null && nextProps.category != null) {
            if (prevProps.category.id !== nextProps.category.id) {
                return false;
            }
        }
        if (prevProps.langCd !== nextProps.langCd) {
            return false;
        }

        return true;
    }
);
